import React, { useState, useEffect, useRef } from 'react';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const parseMonthInput = (input) => {
  if (!input) return '';
  const s = input.trim();

  // Already YYYY-MM
  if (/^\d{4}-\d{2}$/.test(s)) return s;

  // MM/YYYY or M/YYYY or MM-YYYY
  const slashFull = s.match(/^(\d{1,2})[\/\-](\d{4})$/);
  if (slashFull) {
    const m = slashFull[1].padStart(2, '0');
    if (parseInt(m) >= 1 && parseInt(m) <= 12) return `${slashFull[2]}-${m}`;
  }

  // MM/YY or M/YY
  const slashShort = s.match(/^(\d{1,2})[\/\-](\d{2})$/);
  if (slashShort) {
    const m = slashShort[1].padStart(2, '0');
    if (parseInt(m) >= 1 && parseInt(m) <= 12) return `20${slashShort[2]}-${m}`;
  }

  // "June 2025" / "Jun 2025" / "June, 2025" / "Jun. 2025"
  const nameYear = s.match(/^([A-Za-z]+)\.?,?\s+(\d{4})$/);
  if (nameYear) {
    const idx = MONTH_NAMES.findIndex(n => n.toLowerCase().startsWith(nameYear[1].toLowerCase()));
    if (idx >= 0) return `${nameYear[2]}-${String(idx + 1).padStart(2, '0')}`;
  }

  // "June 25" / "Jun 25" — month name + 2-digit year
  const nameYearShort = s.match(/^([A-Za-z]+)\.?,?\s+(\d{2})$/);
  if (nameYearShort) {
    const idx = MONTH_NAMES.findIndex(n => n.toLowerCase().startsWith(nameYearShort[1].toLowerCase()));
    if (idx >= 0) return `20${nameYearShort[2]}-${String(idx + 1).padStart(2, '0')}`;
  }

  return s;
};

const isValidMonth = (yyyymm) => {
  if (!yyyymm) return false;
  if (!/^\d{4}-\d{2}$/.test(yyyymm)) return false;
  const [year, month] = yyyymm.split('-').map(Number);
  return month >= 1 && month <= 12 && year >= 2000 && year <= 2099;
};

const formatMonthForDisplay = (yyyymm) => {
  if (!yyyymm) return '';
  const match = yyyymm.match(/^(\d{4})-(\d{2})$/);
  if (!match) return yyyymm;
  const idx = parseInt(match[2]) - 1;
  if (idx < 0 || idx > 11) return yyyymm;
  return `${MONTH_NAMES[idx]} ${match[1]}`;
};

const parseIncomeInput = (input) => {
  if (!input && input !== 0) return '';
  const cleaned = String(input).replace(/[$,\s]/g, '');
  const val = parseFloat(cleaned);
  if (isNaN(val)) return String(input);
  return String(Math.round(val * 100) / 100);
};

// Display string WITHOUT the $ (a fixed "$" prefix is rendered in the input).
// e.g. 16000 -> "16,000.00"
const formatIncomeNumber = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const num = parseFloat(String(value).replace(/[$,\s]/g, ''));
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Convert a stored record ({ month: 'YYYY-MM', income: number }) into the
// editable display strings the inputs hold ("June 2025", "16,000.00").
const toDisplayRow = (d) => {
  const parsedMonth = parseMonthInput(String(d?.month ?? ''));
  return {
    month: isValidMonth(parsedMonth)
      ? formatMonthForDisplay(parsedMonth)
      : (d?.month ? String(d.month) : ''),
    income: (d?.income !== undefined && d?.income !== null && d?.income !== '')
      ? formatIncomeNumber(d.income)
      : ''
  };
};

const IncomeByMonthForm = ({ onSave, onCancel, existingData = [] }) => {
  const getInitialRows = () => {
    if (existingData && existingData.length > 0) {
      return existingData.map(toDisplayRow);
    }
    return [{ month: '', income: '' }];
  };

  const [rows, setRows] = useState(getInitialRows);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const rowRefs = useRef({});
  const pendingScrollRef = useRef(false);

  useEffect(() => {
    if (existingData && existingData.length > 0) {
      setRows(existingData.map(toDisplayRow));
    }
  }, [existingData]);

  const addRow = () => {
    pendingScrollRef.current = true;
    setRows([...rows, { month: '', income: '' }]);
  };
  const addMultipleRows = (count) => {
    pendingScrollRef.current = true;
    setRows([...rows, ...Array.from({ length: count }, () => ({ month: '', income: '' }))]);
  };

  // Auto-scroll to the newly added row(s) after they render
  useEffect(() => {
    if (!pendingScrollRef.current) return;
    pendingScrollRef.current = false;
    const el = rowRefs.current[rows.length - 1];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [rows.length]);

  const removeRow = (index) => {
    if (rows.length <= 1) return;
    setRows(rows.filter((_, i) => i !== index));
    setErrors(prev => {
      const updated = {};
      Object.keys(prev).forEach(key => {
        const [ri, field] = key.split('.');
        const ri2 = parseInt(ri);
        if (ri2 < index) updated[key] = prev[key];
        else if (ri2 > index) updated[`${ri2 - 1}.${field}`] = prev[key];
      });
      return updated;
    });
  };

  const updateField = (index, field, value) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
    const key = `${index}.${field}`;
    if (errors[key]) {
      const newErrors = { ...errors };
      delete newErrors[key];
      setErrors(newErrors);
    }
  };

  const handleMonthBlur = (index, rawValue) => {
    const parsed = parseMonthInput(rawValue);
    if (isValidMonth(parsed)) {
      updateField(index, 'month', formatMonthForDisplay(parsed));
    } else {
      updateField(index, 'month', rawValue.trim());
    }
  };

  // On focus, strip formatting back to a plain number so it's easy to edit.
  const handleIncomeFocus = (index) => {
    const cleaned = parseIncomeInput(rows[index].income);
    if (cleaned !== '' && !isNaN(parseFloat(cleaned))) {
      updateField(index, 'income', cleaned);
    }
  };

  const handleIncomeBlur = (index, rawValue) => {
    const cleaned = parseIncomeInput(rawValue);
    if (cleaned !== '' && !isNaN(parseFloat(cleaned)) && parseFloat(cleaned) >= 0) {
      updateField(index, 'income', formatIncomeNumber(cleaned));
    } else {
      updateField(index, 'income', rawValue.trim());
    }
  };

  const validate = () => {
    const newErrors = {};
    const seenMonths = {};
    rows.forEach((row, i) => {
      const parsed = parseMonthInput(row.month);
      if (!parsed || !isValidMonth(parsed)) {
        newErrors[`${i}.month`] = 'Enter a valid month (e.g., June 2025, 06/2025)';
      } else {
        if (seenMonths[parsed] !== undefined) {
          newErrors[`${i}.month`] = 'Duplicate month';
          newErrors[`${seenMonths[parsed]}.month`] = 'Duplicate month';
        } else {
          seenMonths[parsed] = i;
        }
      }
      const incomeVal = parseFloat(parseIncomeInput(row.income));
      if (row.income === '' || isNaN(incomeVal)) {
        newErrors[`${i}.income`] = 'Required';
      } else if (incomeVal < 0) {
        newErrors[`${i}.income`] = 'Must be ≥ 0';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const data = rows.map(r => ({
        month: parseMonthInput(r.month),
        income: Math.round(parseFloat(parseIncomeInput(r.income)) * 100) / 100
      }));
      await onSave(data);
    } catch (e) {
      console.error('Save failed:', e);
      setErrors({ form: 'Failed to save. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const validRowCount = rows.filter(r => {
    const pm = parseMonthInput(r.month);
    const pi = parseIncomeInput(r.income);
    return isValidMonth(pm) && pi !== '' && !isNaN(parseFloat(pi));
  }).length;

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Enter Monthly Income Data</h2>
      <p className="text-gray-600 mb-1 text-sm">
        Enter each month and your total income for that month.
      </p>
      <p className="text-gray-500 mb-4 text-xs italic">
        Month accepts any format: June 2025, 06/2025, 6/25, 06-25.&nbsp;
        Income accepts $1,500 or 1500 — dollar signs and commas are optional.
      </p>

      {errors.form && (
        <div className="mb-4 p-3 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm">
          {errors.form}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={addRow} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold">
          + Add Row
        </button>
        <button onClick={() => addMultipleRows(5)} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-semibold">
          + Add 5 Rows
        </button>
        <button onClick={() => addMultipleRows(10)} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-semibold">
          + Add 10 Rows
        </button>
      </div>

      {/* Column headers */}
      <div className="flex gap-3 items-center mb-2 text-xs font-semibold text-gray-500 uppercase">
        <span className="w-8 text-right">#</span>
        <span className="flex-1">Month</span>
        <span className="w-48">Income</span>
        <span className="w-6"></span>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto mb-6">
        {rows.map((row, i) => (
          <div key={i} ref={(el) => (rowRefs.current[i] = el)} className="flex gap-3 items-start">
            <span className="text-gray-400 text-sm mt-2 w-8 text-right">{i + 1}.</span>

            {/* Month */}
            <div className="flex-1">
              <input
                type="text"
                value={row.month}
                onChange={e => updateField(i, 'month', e.target.value)}
                onBlur={e => handleMonthBlur(i, e.target.value)}
                placeholder="June 2025"
                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                  errors[`${i}.month`] ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
              />
              {errors[`${i}.month`] && (
                <p className="text-red-500 text-xs mt-1">{errors[`${i}.month`]}</p>
              )}
            </div>

            {/* Income */}
            <div className="w-48">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm pointer-events-none">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={row.income}
                  onChange={e => updateField(i, 'income', e.target.value)}
                  onFocus={() => handleIncomeFocus(i)}
                  onBlur={e => handleIncomeBlur(i, e.target.value)}
                  placeholder="16,000"
                  className={`w-full pl-7 pr-3 py-2 border rounded-lg text-sm ${
                    errors[`${i}.income`] ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                />
              </div>
              {errors[`${i}.income`] && (
                <p className="text-red-500 text-xs mt-1">{errors[`${i}.income`]}</p>
              )}
            </div>

            <button
              onClick={() => removeRow(i)}
              disabled={rows.length <= 1}
              className="mt-1 text-red-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed text-lg leading-none"
            >
              &times;
            </button>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center pt-4 border-t">
        <span className="text-gray-600 text-sm">{validRowCount} of {rows.length} rows complete</span>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting || validRowCount === 0}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              isSubmitting || validRowCount === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {isSubmitting ? 'Saving...' : `Save Data (${validRowCount} records)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncomeByMonthForm;
